/**
 * Cue before auto-evaluation video capture starts (last W seconds of a sequence).
 * Plays public `/10 seconds counter.mp3` starting `audioDuration` seconds before
 * the eval window opens, so capture begins when the sound ends.
 * Skip when planned sequence duration <= autoeval window W.
 */

import { publicAssetUrl } from "../config";

export const EVAL_PRE_CAPTURE_SOUND_URL = "/10%20seconds%20counter.mp3";
/** Provisional lead until MP3 metadata loads (~file is ~11.05s). */
export const EVAL_PRE_CAPTURE_COUNTDOWN_FALLBACK_SEC = 11;

let sharedAudio = null;
let cachedDurationSec = null;
let durationLoadPromise = null;
let lastPlayKey = null;
let lastPlayAt = 0;
let onEndedListeners = new Set();
let countdownBlocking = false;
let countdownActiveKey = null;
const gateListeners = new Set();

function notifyCountdownGate() {
  const snapshot = getEvalPreCaptureCountdownGate();
  gateListeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch {
      /* ignore */
    }
  });
}

export function getEvalPreCaptureCountdownGate() {
  return {
    blocking: countdownBlocking,
    playKey: countdownActiveKey,
  };
}

/** Subscribe to countdown gate changes (recording must wait while blocking). */
export function subscribeEvalPreCaptureCountdownGate(listener) {
  if (typeof listener !== "function") return () => {};
  gateListeners.add(listener);
  listener(getEvalPreCaptureCountdownGate());
  return () => gateListeners.delete(listener);
}

function armCountdownGate(key) {
  countdownBlocking = true;
  countdownActiveKey = key != null ? String(key) : null;
  notifyCountdownGate();
}

function clearCountdownGate() {
  countdownBlocking = false;
  countdownActiveKey = null;
  notifyCountdownGate();
}

function getSharedAudio() {
  if (typeof window === "undefined") return null;
  if (!sharedAudio) {
    sharedAudio = new Audio(publicAssetUrl(EVAL_PRE_CAPTURE_SOUND_URL));
    sharedAudio.preload = "auto";
    sharedAudio.volume = 1;
    sharedAudio.playsInline = true;
    sharedAudio.addEventListener("ended", () => {
      clearCountdownGate();
      onEndedListeners.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
      onEndedListeners.clear();
    });
  }
  return sharedAudio;
}

/**
 * Unlock the countdown MP3 under a user gesture (iOS Safari autoplay).
 * @returns {boolean|Promise<boolean>}
 */
export function unlockEvalPreCaptureCountdownAudio() {
  const audio = getSharedAudio();
  if (!audio) return false;
  const wasMuted = audio.muted;
  audio.muted = true;
  try {
    const p = audio.play();
    if (p && typeof p.then === "function") {
      return p
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = wasMuted;
          return true;
        })
        .catch(() => {
          audio.muted = wasMuted;
          return false;
        });
    }
    audio.pause();
    audio.currentTime = 0;
    audio.muted = wasMuted;
    return true;
  } catch {
    audio.muted = wasMuted;
    return false;
  }
}

function readAudioDurationSec(audio) {
  const d = Number(audio?.duration);
  if (!Number.isFinite(d) || d <= 0.25) return null;
  return d;
}

/**
 * Actual MP3 duration in seconds (fractional). Falls back until metadata is ready.
 */
export function getEvalPreCaptureCountdownDurationSec() {
  if (cachedDurationSec != null) return cachedDurationSec;
  const audio = getSharedAudio();
  const d = readAudioDurationSec(audio);
  if (d != null) {
    cachedDurationSec = d;
    return d;
  }
  return EVAL_PRE_CAPTURE_COUNTDOWN_FALLBACK_SEC;
}

/**
 * Integer lead time (seconds of sequence remaining) used for timer comparisons.
 * Rounded so sound end aligns with entering the eval window W.
 */
export function getEvalPreCaptureCountdownLeadSec() {
  return Math.max(1, Math.round(getEvalPreCaptureCountdownDurationSec()));
}

/** Prefetch duration from the MP3; resolves with fractional seconds. */
export function ensureEvalPreCaptureCountdownDuration() {
  if (cachedDurationSec != null) {
    return Promise.resolve(cachedDurationSec);
  }
  if (durationLoadPromise) return durationLoadPromise;

  const audio = getSharedAudio();
  if (!audio) {
    return Promise.resolve(EVAL_PRE_CAPTURE_COUNTDOWN_FALLBACK_SEC);
  }

  durationLoadPromise = new Promise((resolve) => {
    const finish = () => {
      const d =
        readAudioDurationSec(audio) ?? EVAL_PRE_CAPTURE_COUNTDOWN_FALLBACK_SEC;
      cachedDurationSec = d;
      resolve(d);
    };
    if (readAudioDurationSec(audio) != null) {
      finish();
      return;
    }
    const onMeta = () => {
      audio.removeEventListener("error", onErr);
      finish();
    };
    const onErr = () => {
      audio.removeEventListener("loadedmetadata", onMeta);
      cachedDurationSec = EVAL_PRE_CAPTURE_COUNTDOWN_FALLBACK_SEC;
      resolve(cachedDurationSec);
    };
    audio.addEventListener("loadedmetadata", onMeta, { once: true });
    audio.addEventListener("error", onErr, { once: true });
    try {
      audio.load();
    } catch {
      onErr();
    }
  });

  return durationLoadPromise;
}

/**
 * @param {object} opts
 * @param {number|null|undefined} opts.sequenceRemainingSec
 * @param {number|null|undefined} opts.sequenceDurationSec
 * @param {number|null|undefined} opts.autoevalDurationSec
 * @param {string|null|undefined} opts.playKey
 * @param {boolean} [opts.enabled=true]
 * @param {(() => void)|null} [opts.onEnded]
 * @returns {boolean} true if playback started
 */
export function maybePlayEvalPreCaptureCountdown({
  sequenceRemainingSec,
  sequenceDurationSec,
  autoevalDurationSec,
  playKey = null,
  enabled = true,
  onEnded = null,
  onBlocked = null,
} = {}) {
  if (!enabled) return false;
  const rem = Math.floor(Number(sequenceRemainingSec));
  const duration = Math.floor(Number(sequenceDurationSec));
  const windowSec = Math.floor(Number(autoevalDurationSec));
  if (!Number.isFinite(rem) || rem <= 0) return false;
  if (!Number.isFinite(duration) || duration <= 0) return false;
  if (!Number.isFinite(windowSec) || windowSec <= 0) return false;

  // Whole block is inside (or shorter than) the eval window — no "before start" cue.
  if (duration <= windowSec) return false;

  const leadSec = getEvalPreCaptureCountdownLeadSec();
  const cueAt = windowSec + leadSec;
  // Band just before recording: (W, W+lead]
  if (rem > cueAt || rem <= windowSec) return false;

  const key = playKey != null ? String(playKey) : `rem:${cueAt}`;
  const now = Date.now();
  if (key === lastPlayKey && now - lastPlayAt < 2000) return false;

  const audio = getSharedAudio();
  if (!audio) return false;
  try {
    if (typeof onEnded === "function") {
      onEndedListeners.add(onEnded);
    }
    audio.muted = false;
    audio.volume = 1;
    audio.currentTime = 0;
    const p = audio.play();
    lastPlayKey = key;
    lastPlayAt = now;
    armCountdownGate(key);
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        if (typeof onEnded === "function") onEndedListeners.delete(onEnded);
        if (lastPlayKey === key) {
          lastPlayKey = null;
          lastPlayAt = 0;
        }
        clearCountdownGate();
        if (typeof onBlocked === "function") onBlocked();
      });
    }
    return true;
  } catch {
    if (typeof onEnded === "function") onEndedListeners.delete(onEnded);
    clearCountdownGate();
    return false;
  }
}

export function resetEvalPreCaptureCountdownDedupe() {
  lastPlayKey = null;
  lastPlayAt = 0;
  clearCountdownGate();
}
